import { API_BASE } from '../config/apiBase';
import { authHeaders, getSessionToken, throwApiError, SessionExpiredError } from './session';

export type DrivePermission = 'owner' | 'edit' | 'view';
export type SharePermission = 'view' | 'edit';

export interface DriveFolder {
  id: number;
  name: string;
  parentFolderId: number | null;
  ownerUserId: number;
  shareCount?: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/** How a file can be shown in the drive; null means download-only. */
export type DrivePreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'csv' | 'docx';

export interface DriveFile {
  id: number;
  name: string;
  folderId: number | null;
  ownerUserId: number;
  mimeType: string;
  previewKind: DrivePreviewKind | null;
  sizeBytes: number;
  uploadedByUserId: number | null;
  uploadedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DriveListing {
  folder: {
    id: number;
    name: string;
    parentFolderId: number | null;
    ownerUserId: number;
    ownerUsername: string;
  } | null;
  permission: DrivePermission;
  breadcrumb: { id: number; name: string }[];
  folders: DriveFolder[];
  files: DriveFile[];
}

export interface SharedFolder {
  id: number;
  name: string;
  ownerUserId: number;
  ownerUsername: string;
  permission: SharePermission;
  sharedBy: string | null;
  sharedAt: string | null;
  /** Whether the folder reached you by name or through one of your roles. */
  via: 'user' | 'role';
  role: string | null;
}

/** A folder you own that other people can see. */
export interface SharedByMeFolder {
  id: number;
  name: string;
  parentFolderId: number | null;
  userShareCount: number;
  roleShareCount: number;
  roles: string[];
  updatedAt: string | null;
}

export interface FolderShare {
  userId: number;
  username: string;
  email: string;
  permission: SharePermission;
  createdAt: string | null;
}

export interface FolderRoleShare {
  role: string;
  permission: SharePermission;
  /** How many people hold the role today. */
  memberCount: number;
  createdAt: string | null;
}

export interface FolderShareLists {
  shares: FolderShare[];
  roleShares: FolderRoleShare[];
}

/**
 * A row in the Archived tab. Only items archived on purpose are listed, so a
 * folder archived with its contents appears once and restores as a whole.
 */
export interface ArchivedDriveItem {
  kind: 'folder' | 'file';
  id: number;
  name: string;
  /** Null for folders, which have no size of their own. */
  sizeBytes: number | null;
  /** The folder it will go back to, or null for the top level of the drive. */
  location: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
}

export interface FolderOption {
  id: number;
  name: string;
  parentFolderId: number | null;
  ownerUserId: number;
  ownerUsername: string;
}

async function driveFetch<T>(path: string, init: RequestInit, fallbackError: string): Promise<T> {
  if (!getSessionToken()) throw new SessionExpiredError();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers as Record<string, string> | undefined), ...authHeaders() },
  });
  if (!res.ok) await throwApiError(res, fallbackError);
  return (await res.json()) as T;
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

export function fetchDriveItems(folderId?: number | null): Promise<DriveListing> {
  const query = folderId != null ? `?folderId=${encodeURIComponent(folderId)}` : '';
  return driveFetch<DriveListing>(`/api/drive/items${query}`, { method: 'GET' }, 'Failed to load the drive.');
}

export async function fetchSharedFolders(): Promise<{ folders: SharedFolder[]; sharedByMe: SharedByMeFolder[] }> {
  const data = await driveFetch<{ folders: SharedFolder[]; sharedByMe: SharedByMeFolder[] }>(
    '/api/drive/shared',
    { method: 'GET' },
    'Failed to load shared folders.'
  );
  return { folders: data.folders ?? [], sharedByMe: data.sharedByMe ?? [] };
}

export async function fetchFolderOptions(): Promise<FolderOption[]> {
  const data = await driveFetch<{ folders: FolderOption[] }>(
    '/api/drive/folder-options',
    { method: 'GET' },
    'Failed to load folders.'
  );
  return data.folders ?? [];
}

export async function createDriveFolder(name: string, parentFolderId: number | null): Promise<DriveFolder> {
  const data = await driveFetch<{ folder: DriveFolder }>(
    '/api/drive/folders',
    jsonInit('POST', { name, parentFolderId }),
    'Failed to create the folder.'
  );
  return data.folder;
}

export async function updateDriveFolder(
  folderId: number,
  updates: { name?: string; parentFolderId?: number | null }
): Promise<DriveFolder> {
  const data = await driveFetch<{ folder: DriveFolder }>(
    `/api/drive/folders/${folderId}`,
    jsonInit('PATCH', updates),
    'Failed to update the folder.'
  );
  return data.folder;
}

/** Archives the folder and everything inside it. Nothing is deleted; see the Archived tab. */
export function archiveDriveFolder(
  folderId: number
): Promise<{ success: boolean; archivedFolders: number; archivedFiles: number }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/archive`,
    { method: 'POST' },
    'Failed to archive the folder.'
  );
}

export function restoreDriveFolder(
  folderId: number
): Promise<{ success: boolean; restoredFolders: number; restoredFiles: number }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/restore`,
    { method: 'POST' },
    'Failed to restore the folder.'
  );
}

export async function updateDriveFile(
  fileId: number,
  updates: { name?: string; folderId?: number | null }
): Promise<DriveFile> {
  const data = await driveFetch<{ file: DriveFile }>(
    `/api/drive/files/${fileId}`,
    jsonInit('PATCH', updates),
    'Failed to update the file.'
  );
  return data.file;
}

/** Archives the file. The bytes are kept, so a restore brings back the same file. */
export function archiveDriveFile(fileId: number): Promise<{ success: boolean }> {
  return driveFetch(`/api/drive/files/${fileId}/archive`, { method: 'POST' }, 'Failed to archive the file.');
}

export function restoreDriveFile(fileId: number): Promise<{ success: boolean }> {
  return driveFetch(`/api/drive/files/${fileId}/restore`, { method: 'POST' }, 'Failed to restore the file.');
}

export async function fetchArchivedDriveItems(): Promise<ArchivedDriveItem[]> {
  const data = await driveFetch<{ items: ArchivedDriveItem[] }>(
    '/api/drive/archived',
    { method: 'GET' },
    'Failed to load archived items.'
  );
  return data.items ?? [];
}

export function fetchFolderShares(folderId: number): Promise<FolderShareLists> {
  return driveFetch(`/api/drive/folders/${folderId}/shares`, { method: 'GET' }, 'Failed to load sharing.');
}

/** Adds people and/or roles at the given permission. Can be called again at any time. */
export function shareDriveFolder(
  folderId: number,
  grant: { userIds?: number[]; roles?: string[]; permission: SharePermission }
): Promise<FolderShareLists & { notified: number }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/shares`,
    jsonInit('POST', { userIds: grant.userIds ?? [], roles: grant.roles ?? [], permission: grant.permission }),
    'Failed to share the folder.'
  );
}

export function updateFolderShare(
  folderId: number,
  userId: number,
  permission: SharePermission
): Promise<{ success: boolean }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/shares/${userId}`,
    jsonInit('PATCH', { permission }),
    'Failed to update sharing.'
  );
}

export function removeFolderShare(folderId: number, userId: number): Promise<{ success: boolean }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/shares/${userId}`,
    { method: 'DELETE' },
    'Failed to update sharing.'
  );
}

export function updateFolderRoleShare(
  folderId: number,
  role: string,
  permission: SharePermission
): Promise<{ success: boolean }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/role-shares/${encodeURIComponent(role)}`,
    jsonInit('PATCH', { permission }),
    'Failed to update sharing.'
  );
}

export function removeFolderRoleShare(folderId: number, role: string): Promise<{ success: boolean }> {
  return driveFetch(
    `/api/drive/folders/${folderId}/role-shares/${encodeURIComponent(role)}`,
    { method: 'DELETE' },
    'Failed to update sharing.'
  );
}

/** Removes every share on a folder, making it private to its owner again. */
export function stopSharingFolder(folderId: number): Promise<{ success: boolean }> {
  return driveFetch(`/api/drive/folders/${folderId}/shares`, { method: 'DELETE' }, 'Failed to update sharing.');
}

/**
 * Downloads go through a one-time ticket: a browser download is a plain
 * navigation, so it cannot carry the Authorization header the API expects.
 */
export async function downloadDriveFile(fileId: number): Promise<void> {
  const data = await driveFetch<{ ticket: string }>(
    `/api/drive/files/${fileId}/download-ticket`,
    { method: 'POST' },
    'Failed to prepare the download.'
  );
  window.location.href = `${API_BASE}/api/drive/files/${fileId}/download?ticket=${encodeURIComponent(data.ticket)}`;
}

/**
 * A URL an <img>/<video>/<audio>/<iframe> can load directly. The ticket in the
 * query string stands in for the Authorization header those tags cannot send;
 * unlike a download ticket it stays valid for the session's reading time, since a
 * player re-requests ranges as it seeks.
 */
export async function createDrivePreviewUrl(fileId: number): Promise<{ url: string; mimeType: string }> {
  const data = await driveFetch<{ ticket: string; mimeType: string }>(
    `/api/drive/files/${fileId}/preview-ticket`,
    { method: 'POST' },
    'Failed to open the file.'
  );
  return {
    url: `${API_BASE}/api/drive/files/${fileId}/preview?ticket=${encodeURIComponent(data.ticket)}`,
    mimeType: data.mimeType,
  };
}

/** Head of a text or CSV file, with a flag when the rest was left behind. */
export function fetchDriveFileText(fileId: number): Promise<{ text: string; truncated: boolean }> {
  return driveFetch(`/api/drive/files/${fileId}/text`, { method: 'GET' }, 'Failed to read the file.');
}

/** A Word document as HTML, converted server-side. */
export function fetchDriveDocxHtml(fileId: number): Promise<{ html: string }> {
  return driveFetch(`/api/drive/files/${fileId}/docx`, { method: 'GET' }, 'Failed to open the document.');
}

export interface UploadHandle {
  /** Resolves with the stored file, or rejects if the upload failed or was cancelled. */
  promise: Promise<DriveFile>;
  cancel: () => void;
}

/**
 * Uploads one file with progress. `fetch` cannot report upload progress, so this
 * is the one place the app uses XMLHttpRequest directly.
 */
export function uploadDriveFile(
  file: File,
  folderId: number | null,
  onProgress: (loadedBytes: number) => void
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<DriveFile>((resolve, reject) => {
    const token = getSessionToken();
    if (!token) {
      reject(new SessionExpiredError());
      return;
    }
    const form = new FormData();
    // The folder must be appended before the file: the server reads text fields
    // as they stream past, and anything after the file arrives too late.
    if (folderId != null) form.append('folderId', String(folderId));
    form.append('file', file, file.name);

    xhr.open('POST', `${API_BASE}/api/drive/files`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      let body: { file?: DriveFile; error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText) as { file?: DriveFile; error?: string };
      } catch {
        body = {};
      }
      if (xhr.status === 401) {
        reject(new SessionExpiredError(body.error));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.file) {
        onProgress(file.size);
        resolve(body.file);
        return;
      }
      if (body.error) {
        reject(new Error(body.error));
        return;
      }
      // A 413 with no JSON body did not come from the app — a proxy in front of
      // it rejected the size before the request arrived, so say so rather than
      // leaving a bare status code on screen.
      if (xhr.status === 413) {
        reject(
          new Error(
            `${file.name} was rejected as too large (413) by the server in front of the app, not by the drive itself. ` +
              'A proxy upload limit needs raising.'
          )
        );
        return;
      }
      reject(new Error(`Upload failed (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    xhr.send(form);
  });
  return { promise, cancel: () => xhr.abort() };
}

/** Human-readable size, e.g. "2.4 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}
